import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, combineLatest, EMPTY, fromEvent, Observable, tap, map } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { FinnairBoundFareFamily } from '@fcom/dapi/api/models/finnair-bound-fare-family';
import { TranslatedBenefitRow } from '@fcom/dapi/api/models/translated-benefit-row';
import { stopPropagation } from '@fcom/core/utils/event';
import { NotificationLayout, NotificationTheme } from '@fcom/ui-components/components/notifications/enums';
import { TagTheme } from '@fcom/ui-components/components/tag/interfaces';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { LocationRouteType } from '@fcom/core-api/interfaces/location-routecff.interface';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { TripType } from '@fcom/core/interfaces/triptype.interface';

import { ExtendedFareFamily, FareFamilyBadgeType } from '../../../interfaces/flight-selection.interface';
import { shouldDisplayBenefit } from '../../../utils/benefit.utils';
import { TaggableComponent } from '../../../../ui-components/components/tag/taggable.component';

@Component({
  selector: 'fin-fare-family-option',
  styleUrls: ['./fare-family-option.component.scss'],
  templateUrl: './fare-family-option.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AirBoundsFareFamilyOptionComponent extends TaggableComponent implements OnInit {
  private mediaQueryService = inject(MediaQueryService);
  readonly isMobile = this.mediaQueryService.isBreakpoint('mobile');
  private configService = inject(ConfigService);
  private el = inject(ElementRef);

  destroyRef = inject(DestroyRef);

  expandOpen = false;

  @Input() context: string;

  @Input() accordionId: string;

  @Input() currencyCode$: Observable<string>;

  @Input()
  set fareFamily(fareFamily: ExtendedFareFamily) {
    this.selected$.next(fareFamily.selected);
    this.filterBenefits(fareFamily);
  }

  @Input() routeType: LocationRouteType;

  @Input() isShortHaul: boolean;

  @Input() hasSpecialOffer = false;

  @Input() originCountryCode: string;

  @Input() destinationCountryCode: string;

  @Input() departureDate: string;

  @Input() tripType$: Observable<TripType> = EMPTY;

  @Input() showKoreaNotification: boolean;

  @Output() selectFareFamily: EventEmitter<FinnairBoundFareFamily> = new EventEmitter();
  @Output() showDimensionsAndDetails: EventEmitter<FinnairBoundFareFamily> = new EventEmitter();

  extendedFareFamily: ExtendedFareFamily;
  selected$ = new BehaviorSubject(false);
  tagTheme$: Observable<TagTheme>;
  highlightedBenefits: TranslatedBenefitRow[] = [];
  isHover$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  isNotAward: boolean;
  enableBenefitChanges: boolean;

  readonly FareFamilyBadgeType = FareFamilyBadgeType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagTheme = TagTheme;

  ngOnInit(): void {
    const isAward = !!this.extendedFareFamily.points;
    this.isNotAward = !isAward;
    this.enableBenefitChanges = this.configService.cfg.enableTicketTypeCardChanges;

    fromEvent(this.el.nativeElement, 'mouseenter')
      .pipe(
        tap(() => this.isHover$.next(true)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    fromEvent(this.el.nativeElement, 'mouseleave')
      .pipe(
        tap(() => this.isHover$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.tagTheme$ = combineLatest([this.isHover$, this.selected$]).pipe(
      map(([isHover, selected]) => {
        if (selected) {
          return TagTheme.DEFAULT;
        }

        if (isHover && this.extendedFareFamily.badgeType === FareFamilyBadgeType.CMS) {
          return TagTheme.DEFAULT;
        }

        switch (this.extendedFareFamily.badgeType) {
          case FareFamilyBadgeType.CMS:
            return this.extendedFareFamily.benefits.tags[0].theme as TagTheme;
          case FareFamilyBadgeType.NEW:
            return TagTheme.ACTIVE;
          case FareFamilyBadgeType.POPULAR:
          default:
            return TagTheme.POPULAR;
        }
      })
    );
  }

  handleSelectFareFamilyClick(e: Event, { ...baseBoundFareFamily }: ExtendedFareFamily): void {
    stopPropagation(e);
    this.selectFareFamily.emit(baseBoundFareFamily as FinnairBoundFareFamily);
  }

  private filterBenefits(fareFamily: ExtendedFareFamily) {
    this.extendedFareFamily = fareFamily;
    this.highlightedBenefits = fareFamily.benefits.translatedBenefitRows.filter((benefit) =>
      shouldDisplayBenefit(benefit)
    );
  }

  toggleExpand(isOpen: boolean): void {
    this.expandOpen = isOpen;
  }

  handleShowDetails(fareFamily: ExtendedFareFamily): void {
    this.showDimensionsAndDetails.emit(fareFamily as FinnairBoundFareFamily);
  }
}
